import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Users, MessageSquare, Star, ArrowRight, Search, Plus, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { scoreColor, scoreLabel } from "@/lib/traits";
import { useAuth } from "@/context/AuthContext";
import AddCompanyModal from "@/components/AddCompanyModal";
import { toast } from "sonner";

export default function Companies() {
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/companies", { params: { search } });
    setCompanies(data);
    setLoading(false);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openAdd = () => {
    if (!user) {
      setAuthOpen(true);
      toast.info("Sign in to add a company");
      return;
    }
    setAddOpen(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-2">Organizations</p>
          <h1 className="font-display text-5xl text-slate-900" style={{ fontWeight: 900 }}>Companies</h1>
          <p className="text-slate-500 mt-2">Org-wide leadership scores aggregated across every reviewed supervisor.</p>
        </div>
        <Button data-testid="add-company-btn" onClick={openAdd} className="bg-slate-900 hover:bg-emerald-600 rounded-full gap-2 shrink-0">
          <Plus size={16} /> Add your company
        </Button>
      </div>

      <div className="relative mb-8 max-w-xl">
        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="company-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search companies by name..."
          className="pl-11 h-12 border-slate-300"
        />
      </div>

      {loading ? (
        <p className="text-slate-400 font-mono text-sm" data-testid="companies-loading">Loading...</p>
      ) : companies.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center" data-testid="companies-empty">
          <Building2 className="mx-auto text-slate-300 mb-3" size={40} />
          <p className="text-slate-600 font-medium">No companies match "{search}"</p>
          <p className="text-slate-400 text-sm mt-1 mb-5">Don't see it? Add it and start reviewing your supervisors.</p>
          <Button data-testid="empty-add-company-btn" onClick={openAdd} className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2">
            <Plus size={16} /> Add {search ? `"${search}"` : "a company"}
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="companies-grid">
          {companies.map((c) => (
            <button
              key={c.id || c.name}
              data-testid="company-card"
              onClick={() => navigate(`/company/${encodeURIComponent(c.name)}`)}
              className="text-left bg-white border border-slate-200 rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-emerald-500/40 group"
            >
              <div className="flex items-start justify-between">
                <div className="h-12 w-12 rounded-lg bg-slate-900 flex items-center justify-center group-hover:bg-emerald-600 transition-colors">
                  <Building2 size={22} className="text-white" />
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <Star size={16} fill="#F59E0B" color="#F59E0B" />
                    <span className="font-mono text-lg font-semibold">{c.org_score > 0 ? c.org_score.toFixed(1) : "—"}</span>
                  </div>
                  <span className="text-xs font-medium" style={{ color: scoreColor(c.org_score) }}>{scoreLabel(c.org_score)}</span>
                </div>
              </div>
              <h2 className="font-display text-2xl text-slate-900 mt-4" style={{ fontWeight: 700 }}>{c.name}</h2>
              {(c.industry || c.location) && (
                <div className="flex items-center gap-2 mt-1 text-xs font-mono uppercase tracking-wider text-slate-400">
                  {c.industry && <span>{c.industry}</span>}
                  {c.industry && c.location && <span className="text-slate-300">•</span>}
                  {c.location && <span className="flex items-center gap-1"><MapPin size={11} /> {c.location}</span>}
                </div>
              )}
              <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><Users size={14} /> {c.supervisor_count} leaders</span>
                <span className="flex items-center gap-1.5"><MessageSquare size={14} /> {c.review_count} reviews</span>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-sm text-emerald-700 font-medium">
                View company <ArrowRight size={15} />
              </div>
            </button>
          ))}
        </div>
      )}

      <AddCompanyModal open={addOpen} onOpenChange={setAddOpen} initialName={companies.length === 0 ? search : ""} onCreated={load} />
    </div>
  );
}
